import * as React from 'react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface ComboboxItem {
  value: string
  label: string
}

interface ComboboxProps<T extends ComboboxItem> {
  items: T[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  getLabel?: (item: T) => string
  emptyText?: string
  className?: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  disabled?: boolean
}

export function Combobox<T extends ComboboxItem>({
  items,
  value,
  onChange,
  placeholder = 'Select...',
  getLabel = (item) => item.label,
  emptyText = 'No item found.',
  searchPlaceholder = 'Search...',
  className,
  searchValue,
  onSearchChange,
  disabled = false,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false)
  const [selectedLabel, setSelectedLabel] = React.useState<string>('')

  React.useEffect(() => {
    setOpen(false)
  }, [value])

  React.useEffect(() => {
    const found = items.find((item) => item.value === value)
    if (found) {
      setSelectedLabel(getLabel(found))
    } else if (!value) {
      setSelectedLabel('')
    }
  }, [value, items, getLabel])

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={disabled ? false : open}
          disabled={disabled}
          className={cn('h-9 w-full min-w-0 justify-between', className)}
        >
          <span className="truncate">
            {value ? selectedLabel : placeholder}
          </span>
          <ChevronsUpDownIcon data-icon="inline-end" className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0">
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={onSearchChange}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  keywords={[getLabel(item)]}
                  onSelect={(currentValue) => {
                    const selectedItem = items.find(
                      (i) => i.value === currentValue,
                    )
                    if (selectedItem) {
                      onChange(currentValue === value ? '' : currentValue)
                    }
                    setOpen(false)
                  }}
                >
                  <CheckIcon
                    className={cn(
                      value === item.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {getLabel(item)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
